import { Injectable } from '@angular/core';
import { FsCryptService, ProfileService } from '@fairandsmart/angular';
import { SessionKey } from '@fairandsmart/types';
import { LocalStorageService } from './local-storage.service';
import { OrgaService } from './orga.service';
import { BackgroundService } from '../workers/worker.service';

const TYPE_PREFIX = '$$type';
const CSK_KEY = 'csk-key';

@Injectable()
export class SecureLocalStorageService {
  constructor(
    private localStorageService: LocalStorageService,
    private cryptService: FsCryptService,
    private backgroundService: BackgroundService,
    private orgaService: OrgaService,
    private profile: ProfileService,
  ) {
  }

  getItem<T>(key: string): Promise<T> {
    const item = this.localStorageService.getItem(this.buildKey(key));
    const valueType = this.localStorageService.getItem(this.buildKey(key + TYPE_PREFIX));
    if (item) {
      return this.uncrypt(item).then((uncryptedValue) => {
        if (valueType === 'object') {
          return JSON.parse(uncryptedValue);
        }
        return uncryptedValue;
      });
    }
    return Promise.resolve(undefined);
  }

  setItem(key: string, value: any): Promise<void> {
    let isObject = false;
    let valueToCipher = value;
    if (typeof value === 'object') {
      valueToCipher = JSON.stringify(value);
      isObject = true;
    }
    return this.crypt(valueToCipher).then((cryptedValue) => {
      if (isObject) {
        this.localStorageService.setItem(this.buildKey(key + TYPE_PREFIX), 'object');
        this.localStorageService.setItem(this.buildKey(key), cryptedValue);
      } else {
        this.localStorageService.setItem(this.buildKey(key), cryptedValue);
      }
    });
  }

  removeItem(key: string) {
    this.localStorageService.removeItem(this.buildKey(key));
    this.localStorageService.removeItem(this.buildKey(key + TYPE_PREFIX));
  }

  private crypt(value: any): Promise<any> {
    return this.getCSK().then((csk) => this.cryptService.cipher(value, csk));
  }

  private uncrypt(value: string): Promise<any> {
    return this.getCSK().then((csk) => this.cryptService.decipher(value, csk));
  }

  private getCSK(): Promise<SessionKey> {
    const csk = this.localStorageService.getItem(this.buildKey(CSK_KEY));
    if (csk) {
      return Promise.resolve(JSON.parse(csk));
    }
    return this.backgroundService.generateCSK().toPromise().then((key) => {
      this.localStorageService.setItem(this.buildKey(CSK_KEY), JSON.stringify(key));
      return key;
    });
  }

  private buildKey(key: string): string {
    return `${key}/${this.orgaService.id}/${this.profile.id}`;
  }
}
